import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { posApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

export function PosShiftsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['pos', 'currentShift'],
    queryFn: posApi.getCurrentShift,
  });

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return (
    <div className="p-6">
      <PageHeader title="POS Shifts" subtitle="FR-POS-02 — Shift management" />

      {data ? (
        <ShiftDetail shiftId={data.id} summary={data} />
      ) : (
        <div className="card p-6">
          <EmptyState message="No open shift for current user." />
        </div>
      )}

      <TerminalsCard />
    </div>
  );
}

function TerminalsCard() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const termsQ = useQuery({ queryKey: ['pos', 'terminals'], queryFn: posApi.listTerminals });

  const deleteMut = useMutation({
    mutationFn: (id: string) => posApi.deleteTerminal(id),
    onSuccess: () => { toast.success('Terminal deleted'); qc.invalidateQueries({ queryKey: ['pos', 'terminals'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="card p-5 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold text-ink-900">POS Terminals</h3>
          <p className="text-xs text-ink-500">Registers available for opening shifts.</p>
        </div>
        <button className="btn-secondary" onClick={() => setShowCreate(true)}>+ New terminal</button>
      </div>
      {(termsQ.data ?? []).length === 0 ? (
        <EmptyState message="No terminals yet." hint="Add a terminal so cashiers can open shifts." />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-ink-500 border-b border-ink-100">
              <th className="py-2">Code</th><th>Name</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {(termsQ.data ?? []).map((t: any) => (
              <tr key={t.id} className="border-b border-ink-50">
                <td className="py-2 font-mono text-xs font-semibold text-ink-900">{t.code}</td>
                <td className="text-ink-800">{t.name}</td>
                <td><span className={`text-2xs px-2 py-0.5 rounded-full ${t.status === 'active' ? 'bg-sage-50 text-sage-700' : 'bg-canvas-subtle text-ink-500'}`}>{t.status}</span></td>
                <td className="text-right">
                  <button className="text-ink-400 hover:text-danger px-1.5 py-1 rounded transition-colors" title="Delete terminal"
                    onClick={() => { if (confirm(`Delete terminal ${t.code}?`)) deleteMut.mutate(t.id); }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showCreate && <TerminalModal
        onClose={() => setShowCreate(false)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ['pos', 'terminals'] }); setShowCreate(false); }} />}
    </div>
  );
}

function TerminalModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  const mut = useMutation({
    mutationFn: () => posApi.createTerminal({
      code: code.trim() ? code.trim().toUpperCase() : undefined,
      name: name.trim(),
    }),
    onSuccess: (r: any) => { toast.success(`Terminal created · ${r.code}`); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
      <h3 className="text-lg font-bold text-ink-900 mb-4">New POS terminal</h3>
      <div className="space-y-3">
        <div>
          <label className="label">Code <span className="text-ink-400 font-normal">(auto if blank)</span></label>
          <input className="input font-mono uppercase" value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Auto (POS-001)" />
        </div>
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Front Counter" />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name.trim() || mut.isPending}
          onClick={() => mut.mutate()}>{mut.isPending ? 'Creating…' : 'Create terminal'}</button>
      </div>
    </Modal>
  );
}

function ShiftDetail({ shiftId, summary }: { shiftId: string; summary: any }) {
  const qc = useQueryClient();

  const zReportQuery = useQuery({
    queryKey: ['pos', 'shiftSummary', shiftId],
    queryFn: () => posApi.shiftSummary(shiftId),
    refetchInterval: 30_000,
  });

  const [showAudit, setShowAudit] = useState(false);

  return (
    <>
      {/* Current shift strip */}
      <div className="card p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div><span className="text-slate-500">Terminal:</span> <span className="font-medium">{summary.terminal_code}</span></div>
        <div><span className="text-slate-500">Branch:</span> <span className="font-medium">{summary.branch_name}</span></div>
        <div><span className="text-slate-500">Opened:</span> <span className="font-medium">{new Date(summary.opened_at).toLocaleString()}</span></div>
        <div><span className="text-slate-500">Float:</span> <span className="font-medium">{fmtCurrency(parseFloat(summary.opening_float))}</span></div>
      </div>

      {zReportQuery.isLoading ? (
        <LoadingState />
      ) : zReportQuery.error ? (
        <ErrorState error={zReportQuery.error} />
      ) : zReportQuery.data ? (
        <>
          <div className="flex items-start justify-between mb-3">
            <h2 className="font-semibold text-lg">Z-Report (live)</h2>
            <button className="btn-secondary text-sm" onClick={() => setShowAudit(true)}>
              Mid-shift cash audit
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Completed sales" value={String(zReportQuery.data.sales.completedCount)} />
            <Stat label="Voided sales" value={String(zReportQuery.data.sales.voidedCount)}
                  tone={zReportQuery.data.sales.voidedCount > 0 ? 'warning' : 'neutral'} />
            <Stat label="Gross revenue" value={`${fmtCurrency(zReportQuery.data.sales.grossRevenue)}`} />
            <Stat label="Net revenue" value={`${fmtCurrency(zReportQuery.data.sales.netRevenue)}`} />
            <Stat label="Total discount" value={`-${fmtCurrency(zReportQuery.data.sales.totalDiscount)}`} />
            <Stat label="Total tax" value={`${fmtCurrency(zReportQuery.data.sales.totalTax)}`} />
            <Stat label="Price overrides" value={String(zReportQuery.data.priceOverrideCount)}
                  tone={zReportQuery.data.priceOverrideCount > 0 ? 'warning' : 'neutral'} />
            <Stat label="Audits so far" value={String(zReportQuery.data.midShiftAudits?.length ?? 0)} />
          </div>

          <h3 className="font-semibold mb-2">Payments by method</h3>
          <div className="card overflow-x-auto mb-6">
            <table className="table">
              <thead>
                <tr>
                  <th>Method</th>
                  <th className="text-right">Count</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(zReportQuery.data?.paymentsByMethod?.length ?? 0) === 0 ? (
                  <tr><td colSpan={3}><EmptyState message="No payments yet." /></td></tr>
                ) : (zReportQuery.data?.paymentsByMethod ?? []).map((p: any) => (
                  <tr key={p.method}>
                    <td className="uppercase">{p.method}</td>
                    <td className="text-right">{p.count}</td>
                    <td className="text-right">{fmtCurrency(p.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {zReportQuery.data.midShiftAudits?.length > 0 && (
            <>
              <h3 className="font-semibold mb-2">Mid-shift audits</h3>
              <div className="card overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Auditor</th>
                      <th className="text-right">Counted</th>
                      <th className="text-right">Variance</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(zReportQuery.data?.midShiftAudits ?? []).map((a: any) => (
                      <tr key={a.id}>
                        <td className="text-xs">{new Date(a.created_at).toLocaleString()}</td>
                        <td>{a.auditor_name ?? '—'}</td>
                        <td className="text-right">{fmtCurrency(parseFloat(a.count_total))}</td>
                        <td className={`text-right ${parseFloat(a.variance) !== 0 ? 'text-amber-600 font-semibold' : ''}`}>
                          ${parseFloat(a.variance).toFixed(2)}
                        </td>
                        <td className="text-xs text-slate-500">{a.notes ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : null}

      {showAudit && (
        <AuditModal
          shiftId={shiftId}
          onClose={() => setShowAudit(false)}
          onDone={() => {
            setShowAudit(false);
            qc.invalidateQueries({ queryKey: ['pos', 'shiftSummary', shiftId] });
          }}
        />
      )}
    </>
  );
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'warning' }) {
  return (
    <div className="card p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-bold mt-1 ${tone === 'warning' ? 'text-amber-600' : 'text-slate-900'}`}>{value}</div>
    </div>
  );
}

function AuditModal({ shiftId, onClose, onDone }: { shiftId: string; onClose: () => void; onDone: () => void }) {
  const [countTotal, setCountTotal] = useState('');
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => posApi.auditShift(shiftId, parseFloat(countTotal) || 0, notes || undefined),
    onSuccess: (data) => {
      toast.success(`Audit recorded — variance ${fmtCurrency(data.variance)}`);
      onDone();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">POS · Shift</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Mid-shift cash audit</h2>
        <p className="text-sm text-ink-500 mt-1 leading-relaxed">
          Count the cash drawer now. The system compares against expected (opening float + cash payments so far)
          and records any variance.
        </p>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Counted cash total <span className="text-danger">*</span></label>
          <input
            type="number" step="0.01" min="0" autoFocus
            className="input tabular-nums"
            value={countTotal}
            onChange={(e) => setCountTotal(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!countTotal || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Recording…' : 'Record audit'}
        </button>
      </div>
    </Modal>
  );
}
